import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IncidentDTO } from "@app-factory/shared";
import { api, ApiRequestError } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Btn,
  Chip,
  DetailHeader,
  EmptyState,
  Panel,
  inputClass,
  type Tone,
} from "@/design/primitives";
import { useProjectRole, canContribute } from "../projects/project-data";
import { useRealtimeEvent } from "@/realtime/useRealtime";

const SEVERITY_TONE: Record<string, Tone> = {
  P0: "red",
  P1: "red",
  P2: "amber",
  P3: "dim",
};

const EVENT_DOT: Record<string, { color: string; glow: string }> = {
  ALERT: { color: "var(--critical)", glow: "var(--critical)" },
  ACTION: { color: "var(--accent)", glow: "var(--accent)" },
  INFO: { color: "var(--text-secondary)", glow: "transparent" },
  RESOLVED: { color: "var(--positive)", glow: "var(--positive)" },
};

/** Incident detail dle /Design (spec §62): KPI, timeline, reprioritizace, resolve. */
export function IncidentPage() {
  const params = useParams({ strict: false }) as { id: string };
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["incident", params.id],
    queryFn: () => api.incident(params.id),
    retry: (count, err) =>
      !(err instanceof ApiRequestError && err.status === 404) && count < 2,
  });
  useRealtimeEvent((m) => {
    if (
      m.type === "project.section.changed" &&
      (m.section === "incidents" || m.section === "tasks")
    ) {
      void queryClient.invalidateQueries({ queryKey: ["incident", params.id] });
    }
  });

  if (query.error instanceof ApiRequestError) {
    return (
      <Panel className="mx-auto mt-16 max-w-md p-6 text-center">
        <EmptyState title={cs.incidents.notFound} />
        <Link to="/" className="text-[12px]">
          {cs.incidents.backToCommand}
        </Link>
      </Panel>
    );
  }
  const incident = query.data?.incident;
  if (!incident) return null;
  return <IncidentDetail incident={incident} />;
}

function IncidentDetail({ incident }: { incident: IncidentDTO }) {
  const queryClient = useQueryClient();
  const projectQuery = useQuery({
    queryKey: ["projects", incident.projectSlug],
    queryFn: () => api.project(incident.projectSlug),
  });
  const role = useProjectRole(
    projectQuery.data?.project ?? ({ members: [] } as never),
  );
  const contributor = canContribute(role);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["incident", incident.id] });
    void queryClient.invalidateQueries({ queryKey: ["incidents"] });
  };
  const onError = (err: unknown) =>
    setError(err instanceof Error ? err.message : cs.common.error);
  const monitoringMutation = useMutation({
    mutationFn: () =>
      api.updateIncident(incident.id, {
        status: "MONITORING",
        note: note || undefined,
      }),
    onSuccess: () => {
      setNote("");
      invalidate();
    },
    onError,
  });
  const resolveMutation = useMutation({
    mutationFn: () => api.resolveIncident(incident.id, { note }),
    onSuccess: () => {
      setNote("");
      invalidate();
    },
    onError,
  });

  const sevTone = SEVERITY_TONE[incident.severity] ?? "dim";
  const open = incident.status !== "RESOLVED";
  const statusLabel =
    incident.status === "OPEN"
      ? cs.incidents.statusOpen
      : incident.status === "MONITORING"
        ? cs.incidents.statusMonitoring
        : cs.incidents.statusResolved;
  const statusTone: Tone =
    incident.status === "RESOLVED"
      ? "green"
      : incident.status === "MONITORING"
        ? "amber"
        : "red";

  return (
    <div className="af-animate-fade">
      <DetailHeader
        back={
          <Link to="/" style={{ color: "inherit" }}>
            {cs.incidents.backToCommand}
          </Link>
        }
        title={`Incident ${incident.label} — ${incident.title}`}
        meta={
          <>
            <span
              data-testid="incident-severity"
              className={open ? "af-animate-pulse" : undefined}
            >
              <Chip tone={sevTone}>
                {cs.incidents.severity(incident.severity)}
              </Chip>
            </span>
            <span data-testid="incident-status">
              <Chip tone={statusTone}>{statusLabel}</Chip>
            </span>
          </>
        }
      />

      {/* KPI karty */}
      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi
          label={cs.incidents.errRate}
          value={incident.errorRate ?? "—"}
          color={open ? "var(--critical)" : "var(--text-primary)"}
          border={
            open
              ? "color-mix(in srgb, var(--critical) 30%, transparent)"
              : undefined
          }
          sub={incident.summary.slice(0, 40) || "—"}
        />
        <Kpi
          label={cs.incidents.affected}
          value={incident.affectedUsers ?? "—"}
          color="var(--text-primary)"
          sub={incident.projectName}
        />
        <Kpi
          label={cs.incidents.duration}
          value={cs.incidents.minutes(incident.durationMinutes)}
          color={open ? "var(--warning)" : "var(--positive)"}
          sub={`od ${new Date(incident.startedAt).toLocaleTimeString("cs-CZ", { hour: "2-digit", minute: "2-digit" })} · ${incident.source}`}
        />
        <Kpi
          label={cs.incidents.businessRisk}
          value={incident.businessImpact ?? "—"}
          color="var(--text-primary)"
          sub={incident.departmentKey ?? "—"}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.3fr_1fr]">
        {/* timeline */}
        <Panel className="p-5" data-testid="incident-timeline">
          <div className="af-label mb-3.5" style={{ color: "var(--accent)" }}>
            {cs.incidents.timeline}
          </div>
          <div className="flex flex-col">
            {incident.events.map((e, i) => {
              const dot = EVENT_DOT[e.kind] ?? {
                color: "var(--text-secondary)",
                glow: "transparent",
              };
              const last = i === incident.events.length - 1;
              return (
                <div key={e.id} className="relative flex gap-3.5 pb-3.5">
                  <div
                    className="w-[52px] flex-none pt-0.5 text-right text-[11px]"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {new Date(e.at).toLocaleTimeString("cs-CZ", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                  <div className="flex w-[10px] flex-none flex-col items-center">
                    <div
                      className={last && open ? "af-animate-pulse" : undefined}
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: dot.color,
                        boxShadow: `0 0 8px ${dot.glow}`,
                      }}
                    />
                    {!last ? (
                      <div
                        className="mt-1 w-px flex-1"
                        style={{
                          background:
                            "color-mix(in srgb, var(--accent) 12%, transparent)",
                        }}
                      />
                    ) : null}
                  </div>
                  <div className="-mt-0.5 flex-1">
                    <div
                      className="text-[12px]"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {e.title}
                    </div>
                    {e.detail ? (
                      <div
                        className="mt-0.5 text-[11px] leading-relaxed"
                        style={{ color: "var(--text-secondary)" }}
                      >
                        {e.detail}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        {/* pravý sloupec */}
        <div className="flex flex-col gap-3.5">
          {open && incident.linkedDecisionId ? (
            <Panel tone="amber" className="p-4" data-testid="incident-decision">
              <div
                className="af-label mb-2"
                style={{ color: "var(--warning)" }}
              >
                {cs.incidents.blockedByYou}
              </div>
              <div
                className="mb-3 text-[11px] leading-relaxed"
                style={{ color: "var(--text-primary)" }}
              >
                Fix čeká na tvé rozhodnutí (deployment policy / eskalace).
              </div>
              <Link
                to="/decisions"
                className="block px-3 py-2 text-center text-[12px] font-bold tracking-[0.08em]"
                style={{
                  background: "var(--accent)",
                  color: "var(--text-on-accent)",
                }}
              >
                {cs.incidents.openDecision}
              </Link>
            </Panel>
          ) : null}

          {incident.status === "RESOLVED" ? (
            <Panel tone="green" className="p-4" data-testid="incident-resolved">
              <div
                className="af-label mb-2"
                style={{ color: "var(--positive)" }}
              >
                {cs.incidents.resolvedPanel}
              </div>
              <div
                className="text-[12px] leading-relaxed"
                style={{ color: "var(--text-primary)" }}
              >
                {incident.resolutionNote}
              </div>
              {incident.postMortemTaskNumber ? (
                <Link
                  to="/projects/$slug/tasks/$number"
                  params={{
                    slug: incident.projectSlug,
                    number: String(incident.postMortemTaskNumber),
                  }}
                  className="mt-2 inline-block text-[11px]"
                  style={{ color: "var(--accent)" }}
                >
                  {cs.incidents.postMortem(incident.postMortemTaskNumber)} →
                </Link>
              ) : null}
            </Panel>
          ) : null}

          <Panel className="p-4" data-testid="incident-reprio">
            <div className="af-label mb-2" style={{ color: "var(--accent)" }}>
              {cs.incidents.reprioritization}
            </div>
            {incident.hotfixTask ? (
              <div
                className="text-[12px] leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {cs.incidents.reprioNote(
                  incident.hotfixTask.epScore,
                  `#${incident.hotfixTask.number}`,
                )}{" "}
                <Link
                  to="/projects/$slug/tasks/$number"
                  params={{
                    slug: incident.projectSlug,
                    number: String(incident.hotfixTask.number),
                  }}
                  style={{ color: "var(--accent)" }}
                >
                  #{incident.hotfixTask.number} {incident.hotfixTask.title} →
                </Link>{" "}
                <span style={{ color: "var(--text-tertiary)" }}>
                  ({incident.hotfixTask.status})
                </span>
              </div>
            ) : (
              <div
                className="text-[12px]"
                style={{ color: "var(--text-tertiary)" }}
              >
                {cs.incidents.noHotfix}
              </div>
            )}
          </Panel>

          {open ? (
            <>
              <Panel className="p-4">
                <div
                  className="af-label mb-2"
                  style={{ color: "var(--accent)" }}
                >
                  {cs.incidents.afterResolve}
                </div>
                <div
                  className="whitespace-pre-line text-[12px] leading-[1.9]"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {cs.incidents.afterResolveSteps}
                </div>
              </Panel>

              {contributor ? (
                <Panel className="p-4" data-testid="incident-actions">
                  <input
                    className={inputClass}
                    placeholder={cs.incidents.resolveNote}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="incident-note"
                  />
                  <div className="mt-2 flex gap-2">
                    {incident.status === "OPEN" ? (
                      <Btn
                        className="flex-1"
                        tone="amber"
                        disabled={monitoringMutation.isPending}
                        onClick={() => monitoringMutation.mutate()}
                        data-testid="incident-monitoring"
                      >
                        {cs.incidents.monitoringBtn}
                      </Btn>
                    ) : null}
                    <Btn
                      className="flex-1"
                      tone="green"
                      disabled={!note.trim() || resolveMutation.isPending}
                      title={
                        !note.trim() ? cs.incidents.resolveNeedsNote : undefined
                      }
                      onClick={() => resolveMutation.mutate()}
                      data-testid="incident-resolve"
                    >
                      {cs.incidents.resolveBtn}
                    </Btn>
                  </div>
                  {!note.trim() ? (
                    <div
                      className="mt-2 text-[11px]"
                      style={{ color: "var(--text-tertiary)" }}
                    >
                      {cs.incidents.resolveNeedsNote}
                    </div>
                  ) : null}
                  {error ? (
                    <div
                      className="mt-2 text-[11px]"
                      style={{ color: "var(--critical)" }}
                    >
                      ⚠ {error}
                    </div>
                  ) : null}
                </Panel>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  color,
  sub,
  border,
}: {
  label: string;
  value: string;
  color: string;
  sub: string;
  border?: string;
}) {
  return (
    <div
      className="px-4 py-3"
      style={{
        borderRadius: "var(--radius-box)",
        border: `1px solid ${border ?? "color-mix(in srgb, var(--accent) 14%, transparent)"}`,
      }}
    >
      <div
        className="text-[11px] tracking-[0.16em]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {label}
      </div>
      <div
        className="af-display mt-0.5 truncate text-[17px] font-bold"
        style={{ color }}
        title={value}
      >
        {value}
      </div>
      <div
        className="truncate text-[11px]"
        style={{ color: "var(--text-tertiary)" }}
      >
        {sub}
      </div>
    </div>
  );
}
